import type { AdminListingResponse } from '@inflip/contracts/admin';
import {
  BRAND_ASSET_LABELS,
  BRAND_LEGAL_USE_LABELS,
  type BrandScope,
  forbiddenIndustryLabel,
  IP_RIGHT_LABELS,
  ListingOfferStatus,
  ListingStatus,
  MODIFICATION_RIGHT_LABELS,
  USAGE_PLACEMENT_LABELS,
  usageScopeRows,
  rateBpLabel,
  VIOLATION_POLICY_LABELS,
  monthlyAddonLabel,
} from '@inflip/shared';
import type { ColumnDef } from '@tanstack/react-table';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DataTable } from '../components/data-table';
import { NoteItems } from '../components/note-items';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  inputCls,
  PageHeader,
  Pager,
  PromptDialog,
} from '../components/ui';
import { api, userApiAssetUrl } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { LISTING_STATUS_META } from '../lib/status-ui';
import { formatDate } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';
import { useSeenVersions } from '../lib/use-seen-versions';

/**
 * 가상 필터 — OFFER_REVIEW: 판매 중 리스팅에 추가돼 단위 심사를 기다리는 오퍼 모아보기,
 * RELIST_REVIEW: 심사 대기 중 예전에 판매했던 IP(판매 재개 심사)만
 */
type ListingFilter = ListingStatus | '' | 'OFFER_REVIEW' | 'RELIST_REVIEW';

const FILTERS: Array<{ key: ListingFilter; label: string }> = [
  { key: ListingStatus.PENDING_REVIEW, label: '심사 대기' },
  { key: 'RELIST_REVIEW', label: '판매 재개 심사' },
  { key: 'OFFER_REVIEW', label: '오퍼 심사' },
  { key: ListingStatus.PUBLISHED, label: '판매 중' },
  { key: ListingStatus.SUSPENDED, label: '중지됨' },
  { key: ListingStatus.ENDED, label: '판매 종료' },
  { key: '', label: '전체' },
];

/** Listing 심사 큐 — 테이블에서 훑고, 행을 펼쳐 상세 확인 후 공개/반려/중지. */
export function ListingsReviewPage() {
  const { session } = useAdminAuth();
  // 목록 상태는 URL 에 — 다른 화면에 다녀와도 보던 필터·검색어가 유지된다
  const [rawFilter, setFilter] = useListViewState<ListingFilter>(
    'filter',
    ListingStatus.PENDING_REVIEW,
  );
  const filter = FILTERS.some((f) => f.key === rawFilter)
    ? rawFilter
    : ListingStatus.PENDING_REVIEW;
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  const [page, setPage] = useListViewState('page', 1);
  const [listings, setListings] = useState<AdminListingResponse[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<
    | null
    | { kind: 'approve'; listing: AdminListingResponse }
    | { kind: 'reject'; listing: AdminListingResponse }
    | { kind: 'approveOffer'; listing: AdminListingResponse; offer: AdminOffer }
    | { kind: 'suspend'; listing: AdminListingResponse }
    | { kind: 'unsuspend'; listing: AdminListingResponse }
    | { kind: 'rejectOffer'; listing: AdminListingResponse; offer: AdminOffer }
  >(null);

  const seen = useSeenVersions();
  const loadedQuery = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    // 오퍼 심사 — 판매 중 리스팅 중 PENDING 오퍼가 달린 것만 (서버가 걸러야 쪽 밖의 대기가 숨지 않는다)
    const query =
      filter === 'OFFER_REVIEW'
        ? { status: ListingStatus.PUBLISHED, pendingOffer: true }
        : filter === 'RELIST_REVIEW'
          ? { status: ListingStatus.PENDING_REVIEW, relisted: true }
          : filter
            ? { status: filter }
            : {};
    // 관리자가 직접 부른 목록(처음·필터·검색·쪽)만 "본 것" — 탭 복귀 토큰 갱신 같은 다시 불러오기는 아니다
    const queryKey = `${filter}|${searchTerm}|${page}`;
    const baseline = loadedQuery.current !== queryKey;
    loadedQuery.current = queryKey;
    return api
      .listListings(session.accessToken, {
        ...query,
        ...(searchTerm ? { q: searchTerm } : {}),
        page,
      })
      .then(({ items: rows, totalPages }) => {
        seen.observe(
          rows.flatMap((l) => [
            [listingKey(l.id), l.updatedAt] as [string, string],
            ...l.offers.map((o) => [offerKey(o.id), o.updatedAt] as [string, string]),
          ]),
          baseline,
        );
        setListings(rows);
        setTotalPages(totalPages);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, filter, searchTerm, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 판매자가 철회하는 등 상태가 바뀌어 거절됐을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  const token = session?.accessToken;

  const columns = useMemo<ColumnDef<AdminListingResponse>[]>(
    () => [
      {
        id: 'expander',
        header: '',
        minSize: 32,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {openId === row.original.id ? '▾' : '▸'}
          </span>
        ),
      },
      {
        id: 'title',
        header: '제목',
        cell: ({ row }) => {
          const l = row.original;
          const stale =
            l.status === ListingStatus.PENDING_REVIEW &&
            seen.isStale(listingKey(l.id), l.updatedAt);
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              {/* 썸네일 — 있으면 목록에서도 작게 보인다 (없으면 자리를 두지 않는다) */}
              {l.thumbnailPath && (
                <img
                  src={userApiAssetUrl(l.thumbnailPath)}
                  alt=""
                  className="aspect-video w-12 shrink-0 rounded-sm bg-muted object-cover"
                />
              )}
              <span className="truncate font-semibold">{l.title}</span>
              {/* 심사 대기면 처음 심사인지, 예전에 판매했던 IP 의 판매 재개 심사인지 */}
              {l.status === ListingStatus.PENDING_REVIEW && <ReviewKindBadge listing={l} />}
              {l.lastSuspendedAt && (
                <span
                  className="shrink-0 whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700"
                  title={l.lastSuspendedReason ?? undefined}
                >
                  중지 이력
                </span>
              )}
              {stale && <ChangedBadge isNew={seen.isNew(listingKey(l.id))} />}
            </span>
          );
        },
      },
      {
        id: 'seller',
        header: '판매자',
        cell: ({ row }) => row.original.sellerName,
        meta: { tdClass: 'whitespace-nowrap text-muted-foreground' },
      },
      {
        id: 'rightTypes',
        header: '권리 종류',
        cell: ({ row }) => (
          <span className="block truncate text-xs text-muted-foreground">
            {row.original.rightTypes.map((t) => IP_RIGHT_LABELS[t]).join(' · ')}
          </span>
        ),
      },
      {
        id: 'price',
        header: '대표가',
        cell: ({ row }) =>
          row.original.priceAmount > 0
            ? `${row.original.priceAmount.toLocaleString('ko-KR')}원~`
            : '협의',
        meta: { tdClass: 'whitespace-nowrap tabular-nums' },
      },
      {
        id: 'status',
        header: '상태',
        cell: ({ row }) => {
          const pendingOffers = row.original.offers.filter(
            (o) => o.status === ListingOfferStatus.PENDING,
          ).length;
          return (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span className={`text-xs font-bold ${LISTING_STATUS_META[row.original.status].cls}`}>
                {LISTING_STATUS_META[row.original.status].label}
              </span>
              {/* 중지 사유 — 다시 열기 전에 왜 막았는지 바로 보이게 */}
              {row.original.status === ListingStatus.SUSPENDED && row.original.suspendedReason && (
                <span
                  className="max-w-[16rem] truncate text-xs text-muted-foreground"
                  title={row.original.suspendedReason}
                >
                  {row.original.suspendedReason}
                </span>
              )}
              {row.original.status === ListingStatus.PUBLISHED && pendingOffers > 0 && (
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                  오퍼 심사 {pendingOffers}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: '등록일',
        cell: ({ row }) => formatDate(row.original.createdAt),
        meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
      },
      {
        id: 'actions',
        header: '액션',
        minSize: 140,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const l = row.original;
          return (
            <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
              {/* 화면이 몰래 다시 불러와 바뀐 심사 — 관리자가 펼쳐 보기 전에는 승인·반려를 내지 않는다 */}
              {l.status === ListingStatus.PENDING_REVIEW &&
                seen.isStale(listingKey(l.id), l.updatedAt) && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={pending}
                    onClick={() => {
                      seen.acknowledge(listingKey(l.id), l.updatedAt);
                      setOpenId(l.id);
                    }}
                  >
                    바뀐 내용 확인
                  </Button>
                )}
              {l.status === ListingStatus.PENDING_REVIEW &&
                !seen.isStale(listingKey(l.id), l.updatedAt) && (
                  <>
                    <Button
                      size="sm"
                      disabled={pending}
                      // 공개는 바로 마켓에 노출된다 — 한 번 더 확인한다
                      onClick={() => setDialog({ kind: 'approve', listing: l })}
                    >
                      승인
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={pending}
                      onClick={() => setDialog({ kind: 'reject', listing: l })}
                    >
                      반려
                    </Button>
                  </>
                )}
              {l.status === ListingStatus.PUBLISHED && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={pending}
                  onClick={() => setDialog({ kind: 'suspend', listing: l })}
                >
                  판매 중지
                </Button>
              )}
              {l.status === ListingStatus.SUSPENDED && (
                <Button
                  size="sm"
                  disabled={pending}
                  onClick={() => setDialog({ kind: 'unsuspend', listing: l })}
                >
                  판매 재개
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openId, pending, token, seen],
  );

  if (!session) return null;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="리스팅"
        description="제출된 IP 를 검토하고 공개·반려·중지를 처리해요"
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-64 ${inputCls}`}
              placeholder="제목 / 판매자 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <Chip
            key={f.key || 'ALL'}
            active={filter === f.key}
            onClick={() => {
              setPage(1);
              setFilter(f.key);
            }}
          >
            {f.label}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {listings.length === 0 ? (
        <EmptyState message="해당 상태의 리스팅이 없어요." />
      ) : (
        <DataTable
          data={listings}
          columns={columns}
          getRowId={(l) => l.id}
          onRowClick={(l) => setOpenId(openId === l.id ? null : l.id)}
          renderExpanded={(l) =>
            openId === l.id ? (
              <ListingDetail
                listing={l}
                pending={pending}
                isOfferStale={(o) => seen.isStale(offerKey(o.id), o.updatedAt)}
                isOfferNew={(o) => seen.isNew(offerKey(o.id))}
                onAcknowledgeOffer={(o) => seen.acknowledge(offerKey(o.id), o.updatedAt)}
                onApproveOffer={
                  l.status === ListingStatus.PUBLISHED
                    ? (offer) => setDialog({ kind: 'approveOffer', listing: l, offer })
                    : undefined
                }
                onRejectOffer={
                  l.status === ListingStatus.PUBLISHED
                    ? (offer) => setDialog({ kind: 'rejectOffer', listing: l, offer })
                    : undefined
                }
              />
            ) : null
          }
        />
      )}
      <Pager page={page} totalPages={totalPages} onChange={setPage} />

      {dialog?.kind === 'approve' && token && (
        <ConfirmDialog
          title="승인하고 공개할까요?"
          description={approveDescription(dialog.listing)}
          confirmLabel="승인"
          busy={pending}
          onConfirm={() =>
            void run(() =>
              api.publishListing(token, dialog.listing.id, {
                seenUpdatedAt: seen.seenOf(listingKey(dialog.listing.id), dialog.listing.updatedAt),
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'approveOffer' && token && (
        <ConfirmDialog
          title={
            dialog.offer.review?.kind === 'NEW'
              ? '추가된 권리를 승인할까요?'
              : '수정된 권리를 승인할까요?'
          }
          description={`"${dialog.listing.title}" 의 ${IP_RIGHT_LABELS[dialog.offer.rightType]} — ${
            dialog.offer.review?.kind === 'NEW' ? '' : '바뀐 조건으로 '
          }바로 판매 중인 상품에 공개되고 판매자에게 알림이 가요.`}
          confirmLabel="승인"
          busy={pending}
          onConfirm={() =>
            void run(() =>
              api.approveListingOffer(token, dialog.listing.id, dialog.offer.id, {
                seenUpdatedAt: seen.seenOf(offerKey(dialog.offer.id), dialog.offer.updatedAt),
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'reject' && token && (
        <PromptDialog
          title="리스팅을 반려할까요?"
          description={`"${dialog.listing.title}" — 반려 사유는 판매자에게 전달돼요.`}
          placeholder="반려 사유"
          submitLabel="반려"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() =>
              api.rejectListing(token, dialog.listing.id, {
                reason,
                seenUpdatedAt: seen.seenOf(listingKey(dialog.listing.id), dialog.listing.updatedAt),
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'rejectOffer' && token && (
        <PromptDialog
          title={
            dialog.offer.review?.kind === 'NEW'
              ? '추가된 권리를 반려할까요?'
              : '수정된 권리를 반려할까요?'
          }
          description={`"${dialog.listing.title}" 의 ${IP_RIGHT_LABELS[dialog.offer.rightType]} — ${offerRejectResult(dialog.offer)} 사유는 판매자에게 전달돼요.`}
          placeholder="반려 사유"
          submitLabel="반려"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() =>
              api.rejectListingOffer(token, dialog.listing.id, dialog.offer.id, {
                reason,
                seenUpdatedAt: seen.seenOf(offerKey(dialog.offer.id), dialog.offer.updatedAt),
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'suspend' && token && (
        <PromptDialog
          title="판매를 중지할까요?"
          description={`"${dialog.listing.title}" 이 마켓에서 내려가고 새 주문·제안을 받지 않아요. 진행 중인 거래는 그대로예요. 사유는 판매자에게 알림으로 전달돼요.`}
          placeholder="중지 사유 (5자 이상)"
          submitLabel="판매 중지"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.suspendListing(token, dialog.listing.id, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'unsuspend' && token && (
        <ConfirmDialog
          title="판매를 다시 시작할까요?"
          description={`"${dialog.listing.title}" 이 다시 마켓에 공개되고 판매자에게 알림이 가요.${
            dialog.listing.suspendedReason ? ` (중지 사유: ${dialog.listing.suspendedReason})` : ''
          }`}
          confirmLabel="판매 재개"
          busy={pending}
          onConfirm={() => void run(() => api.unsuspendListing(token, dialog.listing.id))}
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}

type AdminOffer = AdminListingResponse['offers'][number];

const listingKey = (id: string) => `listing:${id}`;
const offerKey = (id: string) => `offer:${id}`;

/** 처음 심사 / 판매 재개 심사 — 판매 재개면 예전에 공개돼 거래했던 IP */
function ReviewKindBadge({ listing }: { listing: AdminListingResponse }) {
  return listing.firstPublishedAt ? (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
      판매 재개
    </span>
  ) : (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
      신규
    </span>
  );
}

/** 마지막 판매 중지가 아직 안 풀린 채 판매 종료했는지 — 중지 사유가 해결됐는지 심사에서 봐야 한다 */
function endedWhileSuspended(l: AdminListingResponse): boolean {
  if (!l.lastSuspendedAt) return false;
  return !l.lastSuspensionLiftedAt || l.lastSuspensionLiftedAt < l.lastSuspendedAt;
}

function suspensionHistory(l: AdminListingResponse): string | null {
  if (!l.lastSuspendedAt) return null;
  const lifted = !endedWhileSuspended(l);
  return `${formatDate(l.lastSuspendedAt)} 판매 중지${
    l.lastSuspendedReason ? ` (사유: ${l.lastSuspendedReason})` : ''
  } → ${
    lifted && l.lastSuspensionLiftedAt
      ? `${formatDate(l.lastSuspensionLiftedAt)} 어드민이 재개`
      : l.status === ListingStatus.SUSPENDED
        ? '지금 중지 중'
        : '중지된 채 판매 종료'
  }`;
}

function approveDescription(l: AdminListingResponse): string {
  const base = `"${l.title}" 이 바로 마켓에 공개되고 판매자에게 알림이 가요.`;
  if (!l.firstPublishedAt) return base;
  const suspension = suspensionHistory(l);
  return [
    `판매 재개 심사예요 — ${formatDate(l.firstPublishedAt)} 처음 공개${
      l.endedAt ? `, ${formatDate(l.endedAt)} 판매 종료` : ''
    }, 주문 ${l.orderCount}건 · 이용 중 라이선스 ${l.activeLicenseCount}건.`,
    suspension
      ? `⚠ ${suspension}. ${endedWhileSuspended(l) ? '중지 사유가 해결됐는지 확인한 뒤 승인해 주세요. ' : ''}`
      : '',
    base,
  ]
    .filter(Boolean)
    .join(' ');
}

/** 판매 이력 — 처음 공개·판매 종료·중지 이력·거래 수 (한 번도 공개 안 된 IP 는 신규) */
function ListingHistory({ listing: l }: { listing: AdminListingResponse }) {
  const suspension = suspensionHistory(l);
  if (!l.firstPublishedAt && !suspension) {
    return (
      <section>
        <h3 className="mb-1.5 text-xs font-bold text-muted-foreground">판매 이력</h3>
        <p className="text-sm text-muted-foreground">처음 심사받는 IP 예요 (공개된 적 없음)</p>
      </section>
    );
  }
  const rows = [
    l.firstPublishedAt && { label: '처음 공개', value: formatDate(l.firstPublishedAt) },
    l.publishedAt &&
      l.publishedAt !== l.firstPublishedAt && {
        label: '마지막 공개',
        value: formatDate(l.publishedAt),
      },
    l.endedAt && { label: '판매 종료', value: formatDate(l.endedAt) },
    {
      label: '거래',
      value: `주문 ${l.orderCount}건 (취소 제외) · 이용 중 라이선스 ${l.activeLicenseCount}건`,
    },
  ].filter((r): r is { label: string; value: string } => !!r);
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-bold text-muted-foreground">판매 이력</h3>
      <dl className="grid gap-x-6 gap-y-1 rounded-sm bg-muted/40 px-3 py-2 text-sm sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex gap-2">
            <dt className="shrink-0 font-semibold text-muted-foreground">{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>
      {suspension && (
        <p className="mt-2 rounded-sm bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
          {suspension}
        </p>
      )}
    </section>
  );
}

/** 권리 반려의 결과 — 승인 후 수정만 이전 조건으로 돌아가고, 나머지는 상품에서 지운다 */
function offerRejectResult(offer: AdminOffer): string {
  return offer.review?.kind === 'EDITED'
    ? '이전 승인 조건으로 되돌아가 판매가 계속돼요.'
    : '이 권리가 상품에서 삭제돼요.';
}

const OFFER_REVIEW_KIND_LABEL: Record<NonNullable<AdminOffer['review']>['kind'], string> = {
  NEW: '새 권리',
  EDITED: '승인 후 수정',
  EDITED_NO_BASELINE: '승인 후 수정 · 이전 조건 없음',
};

/** 심사 대기 권리가 어떤 심사인지 — 협찬 심사의 "승인 후 수정"과 같은 표시 */
function OfferReviewKindBadge({ offer }: { offer: AdminOffer }) {
  if (!offer.review) return null;
  return (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700">
      {OFFER_REVIEW_KIND_LABEL[offer.review.kind]}
    </span>
  );
}

/** 승인 후 수정 — 이전 승인 조건과 달라진 줄만, 승인된 내용에 줄을 긋고 바뀐 내용은 파란색 (협찬 심사와 같은 모양) */
function OfferReviewChanges({ offer }: { offer: AdminOffer }) {
  if (!offer.review || offer.review.kind === 'NEW') return null;
  if (offer.review.kind === 'EDITED_NO_BASELINE')
    return (
      <p className="mb-2 rounded-sm bg-amber-50 px-3 py-2 text-amber-900">
        승인됐던 권리를 고친 심사인데, 이 기능이 생기기 전에 고쳐 이전 승인 조건이 남아 있지 않아요.
        바뀐 조건을 비교할 수 없고, 반려하면 이 권리가 상품에서 삭제돼요.
      </p>
    );
  const { changes } = offer.review;
  return (
    <section className="mb-2 rounded-sm bg-blue-50/60 px-3 py-2">
      <h4 className="font-bold text-blue-900">
        {changes.length
          ? `바뀐 조건 ${changes.length}개 — 승인된 내용에 줄을 긋고 바뀐 내용을 파란색으로 보여요`
          : '승인된 조건과 달라진 줄이 없어요'}
      </h4>
      {changes.length > 0 && (
        <dl className="mt-1.5 divide-y divide-blue-100">
          {changes.map((c) => (
            <div key={c.label} className="py-1.5">
              <dt className="font-semibold">{c.label}</dt>
              <dd className="mt-0.5 grid gap-0.5 whitespace-pre-wrap break-words">
                <span className="text-muted-foreground line-through">{c.before}</span>
                <span className="font-semibold text-blue-700">{c.after}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-1.5 text-muted-foreground">
        반려하면 이전 승인 조건으로 되돌아가 판매가 계속돼요.
      </p>
    </section>
  );
}

/** 화면을 연 뒤 판매자가 바꿨거나 새로 들어온 심사 */
function ChangedBadge({ isNew }: { isNew: boolean }) {
  return (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">
      {isNew ? '새로 들어옴' : '내용 바뀜'}
    </span>
  );
}

/** 펼침 상세 — 설명 / 주의사항 / 판매 오퍼 테이블 (판매 중 리스팅의 PENDING 오퍼는 단위 심사). */
/** 이름/브랜드 제공 범위 요약 — 기본 포함 · 유료 옵션(+금액) 을 한 줄로 */
function brandScopeSummary(scope: BrandScope | null | undefined): string {
  if (!scope) return '—';
  const labels: Record<string, string> = {
    ...BRAND_ASSET_LABELS,
    ...USAGE_PLACEMENT_LABELS,
    ...BRAND_LEGAL_USE_LABELS,
  };
  const all = [...scope.assets, ...scope.placements, ...scope.legalUses];
  const included = all.filter((o) => o.addonAmount === 0).map((o) => labels[o.key] ?? o.key);
  const addons = all
    .filter((o) => o.addonAmount > 0)
    .map((o) => `${labels[o.key] ?? o.key} ${monthlyAddonLabel(o.addonAmount)}`);
  return [included.join('·'), addons.length ? `옵션 ${addons.join('·')}` : '']
    .filter(Boolean)
    .join(' / ');
}

/** 다시 심사 대상인 계약 조건 — 주문 상세 계약 조건과 같은 라벨 */
function OfferContractTerms({ offer }: { offer: AdminOffer }) {
  const rows = [
    {
      label: '수정 가능 범위',
      value:
        offer.modificationRights.map((key) => MODIFICATION_RIGHT_LABELS[key]).join(', ') || '없음',
    },
    { label: '사전 승인', value: offer.requiresPreApproval ? '필요' : '불필요' },
    {
      label: '금지 업종',
      value: offer.forbiddenIndustries.map((v) => forbiddenIndustryLabel(v)).join(', ') || '없음',
    },
    {
      label: '위반 시 조치',
      value:
        offer.violationPolicies.map((key) => VIOLATION_POLICY_LABELS[key]).join(', ') ||
        '별도 협의',
    },
  ];
  return (
    <dl className="grid gap-x-6 gap-y-1 rounded-sm bg-muted/40 px-3 py-2 sm:grid-cols-2">
      {rows.map((r) => (
        <div key={r.label} className="flex gap-2">
          <dt className="shrink-0 font-semibold text-muted-foreground">{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ListingDetail({
  listing,
  pending = false,
  isOfferStale = () => false,
  isOfferNew = () => false,
  onAcknowledgeOffer,
  onApproveOffer,
  onRejectOffer,
}: {
  listing: AdminListingResponse;
  pending?: boolean;
  /** 화면이 본 적 없는 오퍼 버전 — 확인 전에는 승인·반려를 내지 않는다 */
  isOfferStale?: (offer: AdminOffer) => boolean;
  isOfferNew?: (offer: AdminOffer) => boolean;
  onAcknowledgeOffer?: (offer: AdminOffer) => void;
  onApproveOffer?: (offer: AdminOffer) => void;
  onRejectOffer?: (offer: AdminOffer) => void;
}) {
  const offerActions = Boolean(onApproveOffer ?? onRejectOffer);
  // 판매 재개 심사 — 승인된 권리는 예전 판매 조건 그대로, 대기 권리는 판매 종료 뒤 새로 넣었거나 계약 조건을 바꾼 것
  const relistReview =
    listing.status === ListingStatus.PENDING_REVIEW && Boolean(listing.firstPublishedAt);
  return (
    <div className="space-y-4">
      <ListingHistory listing={listing} />
      <section>
        <h3 className="mb-1.5 text-xs font-bold text-muted-foreground">썸네일</h3>
        {listing.thumbnailPath ? (
          <img
            src={userApiAssetUrl(listing.thumbnailPath)}
            alt={`${listing.title} 썸네일`}
            className="aspect-video w-full max-w-sm rounded-md bg-muted object-cover"
          />
        ) : (
          <p className="text-sm text-muted-foreground">없음 · 권리 이모지 표지로 보여요</p>
        )}
      </section>
      <section>
        <h3 className="mb-1.5 text-xs font-bold text-muted-foreground">설명</h3>
        <p className="whitespace-pre-line text-sm leading-relaxed">{listing.description}</p>
      </section>

      {listing.usageNotes && (
        <section className="rounded-md bg-amber-50 p-4">
          <h3 className="mb-1 text-xs font-bold text-amber-800">판매자 주의·규칙 사항</h3>
          <NoteItems text={listing.usageNotes} className="text-sm text-amber-900" />
        </section>
      )}

      <section>
        <h3 className="mb-2 text-xs font-bold text-muted-foreground">
          판매 오퍼 {listing.offers.length}개
        </h3>
        <div className="overflow-x-auto rounded-md border border-border bg-background">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-3 py-2 font-semibold">권리</th>
                <th className="px-3 py-2 font-semibold">기간 · 가격</th>
                <th className="px-3 py-2 font-semibold">제공 범위</th>
                <th className="px-3 py-2 font-semibold">지역</th>
                <th className="px-3 py-2 font-semibold">독점</th>
                <th className="px-3 py-2 font-semibold">로열티</th>
                <th className="px-3 py-2 font-semibold">심사</th>
              </tr>
            </thead>
            <tbody>
              {listing.offers.map((o) => (
                <Fragment key={o.id}>
                  <tr>
                    <td className="whitespace-nowrap px-3 py-2 font-semibold">
                      {IP_RIGHT_LABELS[o.rightType]}
                    </td>
                    <td className="px-3 py-2">
                      {o.durationOptions
                        .map(
                          (d) =>
                            `${d.months === 0 ? '1회' : `${d.months}개월`}${
                              d.fixedAmount ? ` ${d.fixedAmount.toLocaleString('ko-KR')}원` : ''
                            }`,
                        )
                        .join(' / ')}
                    </td>
                    <td className="px-3 py-2">
                      {brandScopeSummary(o.brandScope)}
                      {o.usageScope && (
                        <dl className="mt-2 space-y-1">
                          {usageScopeRows(o.usageScope).map((r) => (
                            <div key={r.label}>
                              <dt className="font-semibold">{r.label}</dt>
                              <dd>{r.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {o.region === 'DOMESTIC' ? '국내' : '글로벌'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {o.exclusivity === 'NON_EXCLUSIVE'
                        ? '비독점'
                        : o.exclusivity === 'CATEGORY_EXCLUSIVE'
                          ? '카테고리 독점'
                          : '완전 독점'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {o.royaltyRateBp
                        ? `매출 ${rateBpLabel(o.royaltyRateBp)}${
                            o.royaltyPaymentDay ? ` · 매달 ${o.royaltyPaymentDay}일` : ''
                          }`
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {o.status === ListingOfferStatus.PENDING ? (
                        offerActions ? (
                          isOfferStale(o) ? (
                            <span className="flex items-center gap-1.5">
                              <ChangedBadge isNew={isOfferNew(o)} />
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={pending}
                                onClick={() => onAcknowledgeOffer?.(o)}
                              >
                                확인했어요
                              </Button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <span className="font-bold text-amber-700">심사 대기</span>
                              <OfferReviewKindBadge offer={o} />
                              {onApproveOffer && (
                                <Button
                                  size="sm"
                                  disabled={pending}
                                  onClick={() => onApproveOffer(o)}
                                >
                                  승인
                                </Button>
                              )}
                              {onRejectOffer && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={pending}
                                  onClick={() => onRejectOffer(o)}
                                >
                                  반려
                                </Button>
                              )}
                            </span>
                          )
                        ) : (
                          <span className="font-bold text-amber-700">
                            {relistReview ? '새로 추가·조건 변경' : '심사 대기'}
                          </span>
                        )
                      ) : (
                        <span
                          className="text-muted-foreground"
                          title={
                            relistReview
                              ? '계약 조건은 예전에 승인된 그대로예요. 가격은 판매자가 바꿨을 수 있어요.'
                              : undefined
                          }
                        >
                          {relistReview ? '이전 승인 조건' : '승인됨'}
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr className="border-b border-border/60 last:border-0">
                    <td colSpan={7} className="px-3 pb-3 pt-0">
                      <OfferReviewChanges offer={o} />
                      <OfferContractTerms offer={o} />
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
